<template>
  <div class="min-h-screen">
    <header class="sticky top-0 z-50 site-header">
      <div class="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <router-link to="/" class="flex items-center shrink-0">
          <img src="/images/logo.svg" alt="Niva Market Watch" width="159" height="40" class="h-10 w-auto object-contain" />
        </router-link>

        <nav class="hidden lg:flex items-center gap-1 text-sm font-medium">
          <router-link to="/" class="nav-link" exact-active-class="nav-active">Home</router-link>
          <div class="relative group">
            <button class="nav-link flex items-center gap-1" :class="$route.path.startsWith('/ipos') ? 'nav-active' : ''">IPOs <svg class="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg></button>
            <div class="dropdown-menu">
              <router-link to="/ipos/dashboard" class="dropdown-item"><span class="w-5 text-center">📊</span> Dashboard</router-link>
              <router-link to="/ipos" class="dropdown-item"><span class="w-5 text-center">🔥</span> Current IPOs</router-link>
              <router-link to="/ipos/upcoming" class="dropdown-item"><span class="w-5 text-center">📅</span> Upcoming</router-link>
              <router-link to="/ipos/timetable" class="dropdown-item"><span class="w-5 text-center">⏱️</span> Timetable</router-link>
              <router-link to="/ipos/calendar" class="dropdown-item"><span class="w-5 text-center">🗓️</span> Calendar</router-link>
              <router-link to="/ipos/performance" class="dropdown-item"><span class="w-5 text-center">📈</span> Performance</router-link>
              <div class="border-t border-gray-100 my-1"></div>
              <router-link to="/subscriptions" class="dropdown-item"><span class="w-5 text-center">📋</span> Subscription</router-link>
              <router-link to="/ipos/listing-dates" class="dropdown-item"><span class="w-5 text-center">🎯</span> Listing Dates</router-link>
              <router-link to="/allotments" class="dropdown-item"><span class="w-5 text-center">✅</span> Allotment</router-link>
              <div class="border-t border-gray-100 my-1"></div>
              <router-link to="/compare" class="dropdown-item"><span class="w-5 text-center">⚖️</span> Compare IPOs</router-link>
              <router-link to="/screener" class="dropdown-item"><span class="w-5 text-center">🔍</span> IPO Screener</router-link>
            </div>
          </div>
          <router-link to="/gmp" class="nav-link" active-class="nav-active">GMP</router-link>
          <router-link to="/subscriptions" class="nav-link" active-class="nav-active">Subscription</router-link>
          <router-link to="/allotments" class="nav-link" active-class="nav-active">Allotment</router-link>
          <router-link to="/news" class="nav-link" active-class="nav-active">News</router-link>
          <router-link to="/learn" class="nav-link" active-class="nav-active">Learn</router-link>
        </nav>

        <div class="flex items-center gap-3">
          <div class="hidden md:flex items-center search-box rounded-lg px-3 py-2 relative">
            <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            <input v-model="search" @input="onSearch" @keyup.enter="goSearch" @focus="showResults = true" @blur="hideResults" type="text" placeholder="Search IPOs, companies..." class="bg-transparent text-sm outline-none w-44" />
            <div v-if="showResults && searchResults.length" class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-72 overflow-y-auto">
              <router-link v-for="r in searchResults" :key="r.slug" :to="`/ipos/${r.slug}`" class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition" @click="search = ''; showResults = false">
                <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-xs font-bold text-[#2563EB]">{{ r.name?.[0] }}</div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-[#1E293B] truncate">{{ r.name }}</div>
                  <div class="text-xs text-[#64748B]">{{ r.exchange }} • {{ r.status }}</div>
                </div>
                <span v-if="r.latest_gmp?.gmp" class="text-xs font-semibold text-[#16A34A]">₹{{ r.latest_gmp.gmp }}</span>
              </router-link>
            </div>
          </div>
          <button @click="toggleDark" class="dark-toggle" title="Toggle dark mode">
            <svg v-if="!isDark" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"/></svg>
            <svg v-else class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/></svg>
          </button>
          <button @click="mobileOpen = !mobileOpen" class="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
          </button>
        </div>
      </div>

      <div v-if="mobileOpen" class="lg:hidden site-header border-t border-gray-100 px-4 py-4 space-y-1 animate-slide-down">
        <router-link v-for="link in mobileLinks" :key="link.to" :to="link.to" class="block px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm font-medium" @click="mobileOpen=false">{{ link.label }}</router-link>
      </div>
    </header>

    <main class="animate-fade-in"><slot /></main>

    <footer class="bg-[#0F172A] text-gray-400 mt-16">
      <!-- Keep in sync with resources/views/ssr/partials/footer.blade.php -->
      <div class="max-w-7xl mx-auto px-4 py-12">
        <div class="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
          <div class="col-span-2 md:col-span-1">
            <img src="/images/logo-white.svg" alt="Niva Market Watch" width="143" height="36" class="h-9 mb-4" />
            <p class="text-sm leading-relaxed">Track Indian IPOs, GMP, subscription, allotment and listing dates in one place.</p>
          </div>
          <div v-for="col in footerColumns" :key="col.title">
            <p class="text-white font-semibold mb-4 text-sm">{{ col.title }}</p>
            <div class="space-y-2.5 text-sm">
              <router-link v-for="l in col.links" :key="l.to" :to="l.to" class="block py-1 hover:text-white transition">{{ l.label }}</router-link>
            </div>
          </div>
        </div>
        <div class="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>&copy; {{ new Date().getFullYear() }} Niva Market Watch. Information only — not investment advice. GMP figures are unofficial.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const mobileOpen = ref(false);
const search = ref('');
const searchResults = ref([]);
const showResults = ref(false);
const isDark = ref(localStorage.getItem('dark') === 'true');
const router = useRouter();
let searchTimeout = null;

function onSearch() {
  clearTimeout(searchTimeout);
  if (!search.value || search.value.length < 2) { searchResults.value = []; return; }
  searchTimeout = setTimeout(async () => {
    try {
      const { data } = await axios.get(`/api/ipos?search=${search.value}&per_page=6`);
      searchResults.value = data.data || data;
    } catch { searchResults.value = []; }
  }, 300);
}

function goSearch() {
  if (search.value) { router.push(`/search?q=${search.value}`); search.value = ''; showResults.value = false; }
}

function hideResults() { window.setTimeout(() => { showResults.value = false; }, 200); }

function toggleDark() {
  isDark.value = !isDark.value;
  localStorage.setItem('dark', isDark.value);
  document.documentElement.classList.toggle('dark', isDark.value);
}

if (isDark.value) document.documentElement.classList.add('dark');

const mobileLinks = [
  { to: '/', label: 'Home' },
  { to: '/ipos', label: 'Current IPOs' },
  { to: '/ipos/upcoming', label: 'Upcoming IPOs' },
  { to: '/gmp', label: 'GMP' },
  { to: '/subscriptions', label: 'Subscription' },
  { to: '/allotments', label: 'Allotment' },
  { to: '/ipos/calendar', label: 'Calendar' },
  { to: '/compare', label: 'Compare IPOs' },
  { to: '/screener', label: 'IPO Screener' },
  { to: '/news', label: 'News' },
  { to: '/learn', label: 'Learn' },
  { to: '/about', label: 'About' },
];

const footerColumns = [
  { title: 'Markets', links: [
    { to: '/ipos', label: 'Current IPOs' }, { to: '/ipos/upcoming', label: 'Upcoming IPOs' }, { to: '/gmp', label: 'GMP' },
    { to: '/subscriptions', label: 'Subscription' }, { to: '/allotments', label: 'Allotment' }, { to: '/ipos/calendar', label: 'IPO Calendar' },
  ] },
  { title: 'Tools', links: [
    { to: '/ipos/dashboard', label: 'Dashboard' }, { to: '/compare', label: 'Compare IPOs' }, { to: '/screener', label: 'IPO Screener' },
  ] },
  { title: 'Learn & News', links: [
    { to: '/learn', label: 'IPO Guides' }, { to: '/news', label: 'News' },
    { to: '/learn/what-is-ipo-gmp', label: 'What is GMP?' }, { to: '/learn/how-ipo-allotment-works', label: 'How allotment works' },
  ] },
  { title: 'About', links: [
    { to: '/about', label: 'About us' }, { to: '/methodology', label: 'Data methodology' }, { to: '/editorial-policy', label: 'Editorial policy' },
    { to: '/corrections-policy', label: 'Corrections' }, { to: '/disclaimer', label: 'Disclaimer' }, { to: '/contact', label: 'Contact' },
    { to: '/privacy', label: 'Privacy' }, { to: '/terms', label: 'Terms' },
  ] },
];
</script>
